class PrivateLabelCustomizer {
  constructor(section) {
    this.section = section;
    this.input = section.querySelector('[data-private-label-upload]');
    this.status = section.querySelector('[data-private-label-status]');
    this.previews = section.querySelectorAll('[data-private-label-preview]');
    this.dropzone = section.querySelector('[data-private-label-dropzone]');
    this.resetTrigger = section.querySelector('[data-private-label-reset]');
    this.objectUrl = null;
    this.restoring = false;

    if (!this.input || !this.status || !this.previews.length) return;

    // Captured before anything is applied so Remove Logo can put the copy back
    // exactly as the merchant authored it in the theme editor.
    this.defaultStatus = this.status.textContent.trim();

    if (this.resetTrigger) {
      this.resetTrigger.addEventListener('click', () => this.reset());
    }

    section.querySelectorAll('[data-private-label-upload-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', () => this.input.click());
    });

    const sampleTrigger = section.querySelector('[data-private-label-sample-trigger]');
    if (sampleTrigger) {
      sampleTrigger.addEventListener('click', () => {
        if (this.objectUrl) {
          URL.revokeObjectURL(this.objectUrl);
          this.objectUrl = null;
        }
        this.applyPreview(PrivateLabelCustomizer.SAMPLE_LOGO, 'Sample logo');
      });
    }

    this.input.addEventListener('change', () => {
      const file = this.input.files && this.input.files[0];
      if (file) this.handleFile(file);
    });

    if (this.dropzone) {
      ['dragenter', 'dragover'].forEach((eventName) => {
        this.dropzone.addEventListener(eventName, (event) => {
          event.preventDefault();
          this.dropzone.classList.add('dragover');
        });
      });
      ['dragleave', 'drop'].forEach((eventName) => {
        this.dropzone.addEventListener(eventName, (event) => {
          event.preventDefault();
          this.dropzone.classList.remove('dragover');
        });
      });
      this.dropzone.addEventListener('drop', (event) => {
        const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
        if (file) this.handleFile(file);
      });
    }
  }

  setStatus(message, isActive) {
    this.status.textContent = message;
    this.status.classList.toggle('private-label-customizer__status--active', Boolean(isActive));
  }

  clearPreview() {
    this.previews.forEach((preview) => {
      preview.removeAttribute('src');
      preview.alt = '';
      preview.hidden = true;
    });
    this.toggleReset(false);
  }

  toggleReset(hasLogo) {
    if (this.resetTrigger) this.resetTrigger.hidden = !hasLogo;
  }

  // Puts the page back to its first-paint state: nothing previewed, nothing
  // remembered, and the file input emptied so re-picking the same file still
  // fires a change event. Deliberately does not scroll - the visitor is already
  // looking at the control they just pressed.
  reset() {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
    this.input.value = '';
    this.clearPreview();
    PrivateLabelCustomizer.forget();
    this.setStatus(this.defaultStatus);
  }

  handleFile(file) {
    const validMimeTypes = ['image/jpeg', 'image/png'];
    const validExtension = /\.(jpe?g|png)$/i.test(file.name);
    const isSupported = validMimeTypes.includes(file.type) || (!file.type && validExtension);

    if (!isSupported) {
      this.reject('Choose a PNG or JPEG image to preview your logo.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      this.reject('Choose an image smaller than 5 MB to preview your logo.');
      return;
    }

    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = URL.createObjectURL(file);
    this.applyPreview(this.objectUrl, file.name);
    this.remember(file);
  }

  // A rejected file clears the preview, so the remembered logo has to go too -
  // otherwise the mockups read blank while a reload would bring the old logo
  // back.
  reject(message) {
    this.input.value = '';
    this.clearPreview();
    PrivateLabelCustomizer.forget();
    this.setStatus(message);
  }

  remember(file) {
    if (file.size > PrivateLabelCustomizer.PERSIST_LIMIT) {
      PrivateLabelCustomizer.forget();
      this.setStatus(`${file.name} is previewing now, but it is too large to remember between visits.`, true);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (!PrivateLabelCustomizer.store({ src: reader.result, label: file.name })) {
        this.setStatus(`${file.name} is previewing now, but this browser had no room to remember it.`, true);
      }
    };
    reader.onerror = () => PrivateLabelCustomizer.forget();
    reader.readAsDataURL(file);
  }

  restore() {
    const saved = PrivateLabelCustomizer.recall();
    if (!saved || !saved.src) return;
    this.restoring = true;
    try {
      this.applyPreview(saved.src, saved.label || 'Your logo');
      this.setStatus('Your logo was restored from this browser. Preview only — nothing was uploaded.', true);
    } catch (error) {
      PrivateLabelCustomizer.forget();
    }
    this.restoring = false;
  }

  revealPreviews() {
    if (this.restoring) return;
    const target =
      this.section.querySelector('.private-label-customizer__card:not([hidden])') ||
      this.section.querySelector('[data-private-label-results]');
    if (!target) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
  }

  applyPreview(src, label) {
    this.previews.forEach((preview) => {
      preview.src = src;
      preview.alt = `Preview of ${label}`;
      preview.hidden = false;
      preview.classList.remove('private-label-customizer__logo-preview--pulse');
      requestAnimationFrame(() => {
        void preview.offsetWidth;
        preview.classList.add('private-label-customizer__logo-preview--pulse');
      });
    });
    this.toggleReset(true);
    this.setStatus(`${label} shown as a preview only, blended onto each trap. Nothing is uploaded or saved.`, true);
    if (!this.restoring && src.indexOf('data:image/svg+xml') === 0) {
      PrivateLabelCustomizer.store({ src, label });
    }
    this.revealPreviews();
  }
}

// Browser-only memory of the applied logo. localStorage is scoped to this
// origin, so the value is readable across the store but only this section ever
// renders it - and unlike a cookie it is never attached to a request.
PrivateLabelCustomizer.STORAGE_KEY = 'plc:logo:v1';
PrivateLabelCustomizer.PERSIST_LIMIT = 2 * 1024 * 1024;

PrivateLabelCustomizer.recall = function () {
  try {
    return JSON.parse(window.localStorage.getItem(PrivateLabelCustomizer.STORAGE_KEY) || 'null');
  } catch (error) {
    return null;
  }
};

PrivateLabelCustomizer.store = function (value) {
  try {
    window.localStorage.setItem(PrivateLabelCustomizer.STORAGE_KEY, JSON.stringify(value));
    return true;
  } catch (error) {
    return false;
  }
};

PrivateLabelCustomizer.forget = function () {
  try {
    window.localStorage.removeItem(PrivateLabelCustomizer.STORAGE_KEY);
  } catch (error) {
    /* storage unavailable; the preview still works for this visit */
  }
};

// The rep email's preview image: the chosen product with the logo as it is
// shown on the page, redrawn from the same zone styles the mockup is laid out
// with. Returns a promise of a PNG blob, or null when the trigger has no
// product image (the footer CTA).
PrivateLabelCustomizer.snapshot = function (trigger) {
  const card = trigger.closest('.private-label-customizer__card');
  const media = trigger.hasAttribute('data-private-label-detail-rep')
    ? document.querySelector('[data-private-label-detail-panel]:not([hidden]) .private-label-detail__figure')
    : card && card.querySelector('.private-label-customizer__photo');
  const base = media && media.querySelector('img:not([data-private-label-preview])');
  if (!base) return null;
  const zones = Array.from(media.querySelectorAll('[data-private-label-preview]:not([hidden])'));

  const load = (src) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      // Without CORS the product photo would taint the canvas and toBlob throws.
      image.crossOrigin = 'anonymous';
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = src;
    });

  return Promise.all([load(base.currentSrc || base.src)].concat(zones.map((zone) => load(zone.src)))).then(
    ([photo, ...logos]) => {
      const canvas = document.createElement('canvas');
      const width = (canvas.width = photo.naturalWidth);
      const height = (canvas.height = photo.naturalHeight);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(photo, 0, 0, width, height);

      zones.forEach((zone, index) => {
        const logo = logos[index];
        const style = zone.style;
        const deg = (name) => {
          const match = style.transform.match(new RegExp(`${name}\\(([-\\d.]+)`));
          return match ? parseFloat(match[1]) : 0;
        };
        const boxWidth = (parseFloat(style.width) / 100) * width;
        const boxHeight = (parseFloat(style.height) / 100) * height;
        const computed = getComputedStyle(zone);

        ctx.save();
        // Mirrors the zone's CSS: positioned by its centre, then
        // skewY -> skewX -> rotate -> scaleX, in that order.
        ctx.translate((parseFloat(style.left) / 100) * width, (parseFloat(style.top) / 100) * height);
        ctx.transform(1, Math.tan((deg('skewY') * Math.PI) / 180), 0, 1, 0, 0);
        ctx.transform(1, 0, Math.tan((deg('skewX') * Math.PI) / 180), 1, 0, 0);
        ctx.rotate((deg('rotate') * Math.PI) / 180);
        ctx.scale(deg('scaleX') || 1, 1);

        const clip = style.clipPath.match(/polygon\((.*)\)/);
        if (clip) {
          ctx.beginPath();
          clip[1].split(',').forEach((point, i) => {
            const [x, y] = point.trim().split(/\s+/).map(parseFloat);
            const px = -boxWidth / 2 + (x / 100) * boxWidth;
            const py = -boxHeight / 2 + (y / 100) * boxHeight;
            if (i === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
          });
          ctx.closePath();
          ctx.clip();
        }

        // object-fit: contain inside the zone box.
        const scale = Math.min(boxWidth / logo.naturalWidth, boxHeight / logo.naturalHeight);
        const drawWidth = logo.naturalWidth * scale;
        const drawHeight = logo.naturalHeight * scale;
        ctx.globalCompositeOperation = 'multiply';
        ctx.globalAlpha = parseFloat(computed.getPropertyValue('--plc-ink-opacity')) || 1;
        if (computed.filter !== 'none') ctx.filter = computed.filter;
        ctx.drawImage(logo, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
        ctx.restore();
      });

      return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Preview could not be rendered'))), 'image/png');
      });
    }
  );
};

// Simple generated wordmark so "Try a Sample Logo" works without any external file or network request.
PrivateLabelCustomizer.SAMPLE_LOGO =
  'data:image/svg+xml;charset=UTF-8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="240" height="140" viewBox="0 0 240 140">' +
      '<text x="120" y="66" text-anchor="middle" font-family="Arial, sans-serif" font-size="30" font-weight="800" fill="#ce0f2d">SAMPLE</text>' +
      '<text x="120" y="98" text-anchor="middle" font-family="Arial, sans-serif" font-size="17" font-weight="700" letter-spacing="3" fill="#241f20">LOGO CO.</text>' +
    '</svg>'
  );

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-private-label-customizer]').forEach((section) => {
    const customizer = new PrivateLabelCustomizer(section);
    if (customizer.previews && customizer.previews.length) customizer.restore();
  });
});

/* Private label detail widget. Values are rendered into the markup by the
   section from its block settings, so the widget itself holds no product data
   and a missing figure reads as pending rather than as a number. */
(function () {
  const root = document.querySelector('[data-private-label-detail]');
  if (!root) return;

  const dialog = root.querySelector('.private-label-detail__dialog');
  const panels = root.querySelectorAll('[data-private-label-detail-panel]');
  const repButton = root.querySelector('[data-private-label-detail-rep]');
  let lastFocused = null;
  let activePanel = null;

  function open(blockId) {
    let matched = null;
    panels.forEach((panel) => {
      const isMatch = panel.getAttribute('data-private-label-detail-panel') === blockId;
      panel.hidden = !isMatch;
      if (isMatch) matched = panel;
    });
    if (!matched) return;

    activePanel = matched;
    lastFocused = document.activeElement;
    const panelHeading = matched.querySelector('h3[id]');
    dialog.setAttribute('aria-labelledby', panelHeading ? panelHeading.id : 'PrivateLabelDetailTitle');
    if (repButton) {
      repButton.setAttribute('data-product-title', matched.getAttribute('data-product-title') || '');
      repButton.setAttribute('data-product-image', matched.getAttribute('data-product-image') || '');
    }
    root.hidden = false;
    document.body.classList.add('private-label-detail-lock');
    dialog.focus({ preventScroll: true });
  }

  function close(restoreFocus) {
    if (root.hidden) return;
    root.hidden = true;
    activePanel = null;
    document.body.classList.remove('private-label-detail-lock');
    // Skipped when the rep modal is taking over: it has already moved focus to
    // its own dialog, and pulling it back here would strand the user.
    if (restoreFocus !== false && lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus({ preventScroll: true });
    }
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-private-label-details]');
    if (trigger) {
      event.preventDefault();
      open(trigger.getAttribute('data-private-label-details'));
      return;
    }
    if (event.target.closest('[data-private-label-detail-close]')) {
      event.preventDefault();
      close();
    }
  });

  // Hand straight over to the site's standard state selector. Private label
  // keeps that flow; it is deliberately not routed anywhere else.
  const repModal = document.querySelector('[data-sales-rep-quick-add]');
  if (repModal) {
    new MutationObserver(() => {
      if (!repModal.hidden && !root.hidden) close(false);
    }).observe(repModal, { attributes: true, attributeFilter: ['hidden'] });
  }

  document.addEventListener('keydown', (event) => {
    if (root.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(
      root.querySelectorAll('a[href], button:not([disabled])')
    ).filter((element) => {
      const rect = element.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    });
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
}());

/* Product-page logo preview.

   Turns the gallery's main image into the visitor's own branded mockup: the
   featured slide's photo is replaced with the clean mockup and the print areas
   are laid over it, using the same coordinates the visualizer uses.

   Everything happens in the browser and only when a logo is stored, so a
   shopper who has never used the visualizer gets Dawn's gallery untouched. */
(function () {
  function activeMedia() {
    const gallery = document.querySelector('media-gallery') || document;
    return (
      gallery.querySelector('.product__media-item.is-active .product__media') ||
      gallery.querySelector('.product__media-item .product__media')
    );
  }

  function paint() {
    const source = document.querySelector('[data-private-label-pdp]');
    if (!source) return;

    const saved = PrivateLabelCustomizer.recall();
    if (!saved || !saved.src) return;

    const media = activeMedia();
    if (!media) return;
    const photo = media.querySelector('img');
    if (!photo) return;

    // The mockup is square and Dawn sizes this container from the original
    // image's aspect. These products are all square shots, so the box already
    // matches; pinning it makes the zone percentages correct regardless.
    const container = media.closest('.product-media-container');
    if (container) container.style.setProperty('--ratio', '1.0');

    photo.removeAttribute('srcset');
    photo.removeAttribute('sizes');
    const mockup = source.getAttribute('data-mockup');
    photo.src = mockup;
    photo.alt = source.getAttribute('data-mockup-alt') || photo.alt;

    media.classList.add('private-label-media');

    source.querySelectorAll('[data-private-label-preview]').forEach((zone) => {
      const overlay = zone.cloneNode(true);
      overlay.src = saved.src;
      overlay.alt = '';
      overlay.hidden = false;
      media.appendChild(overlay);
    });

    // The thumbnail for this slide would otherwise still show the marker
    // artwork while the main image shows the clean mockup.
    const slide = media.closest('.product__media-item');
    const mediaId = slide && slide.getAttribute('data-media-id');
    if (mediaId) {
      const thumb = document.querySelector('[data-target="' + mediaId + '"] img');
      if (thumb) {
        thumb.removeAttribute('srcset');
        thumb.removeAttribute('sizes');
        thumb.src = mockup;
      }
    }

    const caption = source.getAttribute('data-caption');
    if (caption) {
      // Dawn stretches every direct child of .product__media to fill the box.
      // The zone images escape that because their inline style wins; a plain
      // span does not, so it goes inside a transparent wrapper that absorbs it.
      const wrap = document.createElement('div');
      wrap.className = 'private-label-media__tagwrap';
      const tag = document.createElement('span');
      tag.className = 'private-label-media__tag';
      tag.textContent = caption;
      wrap.appendChild(tag);
      media.appendChild(wrap);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', paint);
  } else {
    paint();
  }
}());

/* Grid density toggle. Mirrors the two-up / one-up switch on the
   catchmaster.com collection grid. The control is hidden above 749px, where
   the grid is four-up and a one-up state has no use, so the class it sets is
   only ever honoured by the phone breakpoint. */
(function () {
  document.querySelectorAll('[data-private-label-customizer]').forEach((section) => {
    const toggle = section.querySelector('.private-label-customizer__view-toggle');
    const grid = section.querySelector('.private-label-customizer__grid');
    if (!toggle || !grid) return;

    toggle.addEventListener('click', (event) => {
      const button = event.target.closest('[data-private-label-view]');
      if (!button) return;

      toggle.querySelectorAll('[data-private-label-view]').forEach((other) => {
        const isActive = other === button;
        other.classList.toggle('is-active', isActive);
        other.setAttribute('aria-pressed', String(isActive));
      });

      grid.classList.toggle(
        'private-label-customizer__grid--single',
        button.getAttribute('data-private-label-view') === 'single'
      );
    });
  });
}());

/* Print-type filter. Cards are only hidden, never removed, so a logo applied
   under one filter is already painted on every card when the filter changes. */
(function () {
  document.querySelectorAll('[data-private-label-customizer]').forEach((section) => {
    const filters = section.querySelector('[data-private-label-filters]');
    const status = section.querySelector('[data-private-label-filter-status]');
    const cards = section.querySelectorAll('.private-label-customizer__card[data-print-type]');
    if (!filters || !cards.length) return;

    filters.addEventListener('click', (event) => {
      const button = event.target.closest('[data-private-label-filter]');
      if (!button) return;
      const type = button.getAttribute('data-private-label-filter');

      filters.querySelectorAll('[data-private-label-filter]').forEach((other) => {
        const isActive = other === button;
        other.classList.toggle('is-active', isActive);
        other.setAttribute('aria-pressed', String(isActive));
      });

      let shown = 0;
      cards.forEach((card) => {
        const match = type === 'all' || card.getAttribute('data-print-type') === type;
        card.hidden = !match;
        if (match) shown += 1;
      });

      if (status) {
        status.textContent = `Showing ${shown} of ${cards.length} products.`;
      }
    });
  });
}());

/* PestWorld giveaway entry, posted straight to Klaviyo's client subscriptions
   endpoint as the catchmaster.com landing pages do. The phone goes in as a
   property only: a phone_number on this endpoint can also record SMS consent,
   which the form's fine print does not ask for. */
function privateLabelGiveawayPayload(entry, list, source, search) {
  const name = entry.name.trim().replace(/\s+/g, ' ');
  const space = name.indexOf(' ');
  const properties = {
    pl_phone: entry.phone.trim(),
    pl_entry_source: new URLSearchParams(search).get('utm_medium') || 'direct',
  };
  if (entry.interest) properties.pl_interest = entry.interest;

  return {
    data: {
      type: 'subscription',
      attributes: {
        custom_source: source,
        profile: {
          data: {
            type: 'profile',
            attributes: {
              email: entry.email.trim(),
              first_name: space === -1 ? name : name.slice(0, space),
              last_name: space === -1 ? undefined : name.slice(space + 1),
              organization: entry.company.trim(),
              properties,
            },
          },
        },
      },
      relationships: { list: { data: { type: 'list', id: list } } },
    },
  };
}

(function () {
  document.querySelectorAll('[data-private-label-giveaway]').forEach((form) => {
    const panel = form.closest('[data-private-label-giveaway-panel]');
    const success = panel.querySelector('[data-private-label-giveaway-success]');
    const error = form.querySelector('[data-private-label-giveaway-error]');
    const button = form.querySelector('[type="submit"]');

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      button.disabled = true;
      error.textContent = '';

      const entry = Object.fromEntries(new FormData(form));
      const payload = privateLabelGiveawayPayload(
        entry,
        form.getAttribute('data-klaviyo-list'),
        form.getAttribute('data-klaviyo-source'),
        window.location.search
      );

      fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(form.getAttribute('data-klaviyo-company'))}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify(payload),
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Klaviyo responded ${response.status}`);
          form.hidden = true;
          success.hidden = false;
          success.focus();
          const { properties } = payload.data.attributes.profile.data.attributes;
          document.dispatchEvent(new CustomEvent('private-label:giveaway-entered', {
            detail: { interest: properties.pl_interest || '', entry_source: properties.pl_entry_source },
          }));
        })
        .catch(() => {
          error.textContent = error.getAttribute('data-message');
          button.disabled = false;
        });
    });
  });
}());
